import { GetDustParamService } from "./dust.service";
import { GetFilenamesService } from "./filenames.service";
import { ParamService } from "./param.service";
import { QfaFile } from "./qfa-file.model";
import * as types from "./qfa-types";
import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

export interface QfaFilename {
  filename: string;
}

export interface QfaResult {
  data: types.data;
  date: string;
  dates: string[];
  parameters: string[];
  file: QfaFilename;
}

@Injectable()
export class QfaService {
  filenamesService = inject(GetFilenamesService);
  dustParamService = inject(GetDustParamService);
  paramService = inject(ParamService);
  private http = inject(HttpClient);

  baseUrl = "https://static.lawinen-warnung.eu/zamg_qfa/";
  dustParams = {};
  coords = {
    muenchen: {
      lng: 11.5,
      lat: 48.1,
    },
    garmischpartenkirchen: {
      lng: 11,
      lat: 47.4,
    },
    gaschurn: {
      lng: 10,
      lat: 46.9,
    },
    galzigarlberg: {
      lng: 10.2,
      lat: 47.1,
    },
    tannheim: {
      lng: 10.5,
      lat: 47.5,
    },
    hahnenkamm: {
      lng: 12.3,
      lat: 47.4,
    },
    krimml: {
      lng: 12.1,
      lat: 47.2,
    },
    rudolfshuette: {
      lng: 12.6,
      lat: 47.1,
    },
    sonnblick: {
      lng: 12.9,
      lat: 47,
    },
    obertauern: {
      lng: 13.5,
      lat: 47.2,
    },
    feuerkogel: {
      lng: 13.7,
      lat: 47.8,
    },
    aigenimennstal: {
      lng: 14.1,
      lat: 47.5,
    },
    lunz: {
      lng: 15,
      lat: 47.8,
    },
    mariazell: {
      lng: 15.3,
      lat: 47.7,
    },
    fischbach: {
      lng: 15.6,
      lat: 47.4,
    },
    raxseilbahn: {
      lng: 15.7,
      lat: 47.7,
    },
    moenichkirchen: {
      lng: 16,
      lat: 47.5,
    },
    mittelberg: {
      lng: 10.1,
      lat: 47.3,
    },
    neumarktersattel: {
      lng: 14.4,
      lat: 47.1,
    },
    schoeckl: {
      lng: 15.4,
      lat: 47.2,
    },
    galtuer: {
      lng: 10.1,
      lat: 46.9,
    },
    schmittenhoehe: {
      lng: 12.7,
      lat: 47.3,
    },
    ramsaudachstein: {
      lng: 13.6,
      lat: 47.4,
    },
    badgoisern: {
      lng: 13.6,
      lat: 47.6,
    },
    windischgarsten: {
      lng: 14.3,
      lat: 47.7,
    },
    badaussee: {
      lng: 13.7,
      lat: 47.6,
    },
    puchberg: {
      lng: 15.9,
      lat: 47.7,
    },
    semmering: {
      lng: 15.8,
      lat: 47.6,
    },
    hohewand: {
      lng: 16,
      lat: 47.8,
    },
    turracherhoehe: {
      lng: 13.8,
      lat: 46.9,
    },
    traunsteinblfu: {
      lng: 12.6,
      lat: 47.8,
    },
    hochschwabeismauerlwd: {
      lng: 15.1,
      lat: 47.6,
    },
    planneralmrotbuehel: {
      lng: 14.2,
      lat: 47.3,
    },
    flachau: {
      lng: 13.3,
      lat: 47.3,
    },
    grossarl: {
      lng: 13.2,
      lat: 47.2,
    },
    damuels: {
      lng: 9.8,
      lat: 47.2,
    },
  };
  files = {
    muenchen: [],
    garmischpartenkirchen: [],
    gaschurn: [],
    galzigarlberg: [],
    tannheim: [],
    hahnenkamm: [],
    krimml: [],
    rudolfshuette: [],
    sonnblick: [],
    obertauern: [],
    feuerkogel: [],
    aigenimennstal: [],
    lunz: [],
    mariazell: [],
    fischbach: [],
    raxseilbahn: [],
    moenichkirchen: [],
    mittelberg: [],
    neumarktersattel: [],
    schoeckl: [],
    galtuer: [],
    schmittenhoehe: [],
    ramsaudachstein: [],
    badgoisern: [],
    windischgarsten: [],
    badaussee: [],
    puchberg: [],
    semmering: [],
    hohewand: [],
    turracherhoehe: [],
    traunsteinblfu: [],
    hochschwabeismauerlwd: [],
    planneralmrotbuehel: [],
    flachau: [],
    grossarl: [],
    damuels: [],
  };
  cities = Object.keys(this.files);

  async loadDustParams() {
    this.dustParams = await this.dustParamService.parseDustParams();
  }

  async getFiles() {
    for (const city of this.cities) {
      const filenames = await this.filenamesService.getFilenames(this.baseUrl, city);
      const parsedFiles = [];
      for (const file of filenames) {
        const parsedFile = this.filenamesService.parseFilename(file.name);
        parsedFiles.push(parsedFile);
      }
      this.files[city] = parsedFiles.filter((el) => el.startDay === "00");
    }

    return this.files;
  }

  async getRun(file: QfaFilename, startDay: number, first: boolean): Promise<QfaResult> {
    const days = `0${startDay}0${startDay + 2}`;
    const filename = file.filename.replace(/\d{4}\.txt/g, `${days}.txt`);
    const run = new QfaFile(this.http);
    await run.loadFromURL(filename);

    const parameters = Object.keys(run.data.parameters);

    if (first) {
      const city = run.data.metadata.location.split(" ")[2].toLowerCase();
      if (this.dustParams) {
        const dust = this.dustParams[city][startDay / 3];
        run.data.parameters["DUST"] = dust;
        parameters.unshift("DUST");
      }
    }

    const qfa = {
      data: run.data,
      date: run.date,
      dates: run.paramDates,
      parameters: parameters,
      file: file,
    };

    return qfa;
  }
}
